import React from 'react';
import { twMerge } from 'tailwind-merge';

export type TypographyVariant = 
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' 
  | 'body' | 'body-sm' | 'body-md' | 'body-xs'
  | 'label' | 'caption';

export type TypographyColor = 
  | 'primary'    // Main text, highest contrast
  | 'secondary'  // Secondary text, medium contrast
  | 'muted'      // Least emphasis, lowest contrast
  | 'inherit';   // Inherit from parent

type ElementType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'label';

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: TypographyVariant;
  color?: TypographyColor;
  component?: ElementType;
  className?: string;
  weight?: string; // Applied via className (e.g. font-medium, font-semibold)
  children: React.ReactNode;
}

const Typography = ({ 
  variant = 'body',
  color = 'primary',
  component,
  className = '',
  weight,
  children,
  ...rest
}: TypographyProps) => {
  // Map variants to appropriate element types if not explicitly provided
  const getComponent = (): ElementType => {
    if (component) return component;
    
    if (variant.startsWith('h')) return variant as ElementType;
    if (variant === 'label') return 'label';
    return 'p';
  };
  
  // Color variants
  const getColorClass = (color: TypographyColor): string => {
    switch (color) {
      case 'primary':
        return 'text-gray-900 dark:text-white/70';
      case 'secondary':
        return 'text-gray-700 dark:text-gray-300';
      case 'muted':
        return 'text-gray-500 dark:text-gray-400';
      case 'inherit':
        return '';
      default:
        return 'text-gray-900 dark:text-white/90';
    }
  };
  
  // Size & weight variants
  const getVariantClasses = (variant: TypographyVariant): string => {
    switch (variant) {
      case 'h1':
        return 'text-3xl font-bold';
      case 'h2':
        return 'text-2xl font-semibold';
      case 'h3':
        return 'text-xl font-semibold';
      case 'h4':
        return 'text-lg font-medium';
      case 'h5':
        return 'text-base font-medium';
      case 'h6':
        return 'text-sm font-medium';
      case 'body':
        return 'text-base';
      case 'body-sm':
        return 'text-sm';
      case 'body-md':
        return 'text-base';
      case 'body-xs':
        return 'text-xs';
      case 'label':
        return 'text-sm font-medium';
      case 'caption':
        return 'text-xs';
      default:
        return 'text-base';
    }
  };

  const Component = getComponent();
  const colorClass = getColorClass(color);
  const variantClass = getVariantClasses(variant);
  const weightClass = weight ? (weight.startsWith('font-') ? weight : `font-${weight}`) : '';
  
  return (
    <Component 
      className={twMerge(variantClass, colorClass, weightClass, className)}
      {...rest}
    >
      {children}
    </Component>
  );
};

export default Typography; 